import {
  ICallInteraction,
  IDebitmanagment,
  IRepossession,
} from "../../types/repossession.type";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";

const makeRequestDebounced = createDebounce(makeRequest, 200);
const API_URL = `${import.meta.env.VITE_API_URL}/debit-management`;

type GetDebitManagementOptions = {
  status?: string[];
  search?: string;
  page?: number;
  itemsPerPage?: number;
  orderBy?: string;
  fields?: string;
  billingAgentName?: string;
};

export type DebitManagementDashboardData = {
  summary: {
    totalCustomers: number;
    totalAmount: number;
    averageDaysOverdue: number;
    maxDaysOverdue: number;
  };
  rankingByCompany: Array<{
    companyId: string;
    companyName: string;
    totalCustomers: number;
    totalAmount: number;
  }>;
  highestDebts: Array<{
    id: string;
    renterName: string;
    licensePlate: string;
    amount: number;
    daysOverdue: number;
  }>;
};

export const getDebitManagement = async (
  options: GetDebitManagementOptions = {}
) => {
  const {
    status = [],
    search = "",
    page = 1,
    itemsPerPage = 10,
    orderBy = "-daysOverdue,-updatedAt",
    fields = "companyId, renter, daysOverdue, amount, status, billingAgentId, billingAgentName, billingAgentEmail, negotiations, createdAt, updatedAt",
    billingAgentName = "",
  } = options;
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });

  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  params.append("pageOptions[page]", String(page));
  params.append("pageOptions[itemsPerPage]", String(itemsPerPage));

  status && params.append("filters[status]", statusParam);
  billingAgentName && params.append("filters[billingAgentName]", billingAgentName);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);

  const url = `${API_URL}/?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IDebitmanagment[]
  > & { totalItems: number };
  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
};

export const getBillingAgents = async (): Promise<string[]> => {
  const params = new URLSearchParams({
    page: "1",
    itemsPerPage: "9999",
    fields: "billingAgentName",
  });
  params.append("pageOptions[page]", "1");
  params.append("pageOptions[itemsPerPage]", "9999");
  params.append("filters[status]", "PENDING");

  const url = `${API_URL}/?${params.toString()}`;
  const result = (await makeRequest(url)) as ApiResponseType<
    IDebitmanagment[]
  >;
  if (!result.ok || !result.data) return [];
  return [
    ...new Set(
      result.data
        .map((item) => item.billingAgentName)
        .filter((name): name is string => !!name)
    ),
  ].sort();
};

export const getDebitManagementDashboard = async () => {
  const url = `${API_URL}/dashboard`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<DebitManagementDashboardData>;
  return { ok: result.ok, data: result.data };
};

export const createCallInteractions = async (
  debitmanagmentId: string,
  negotiationsId: string,
  callInteraction: ICallInteraction
) => {
  const url = `${API_URL}/create-callInteractions`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      debitmanagmentId,
      negotiationsId,
      callInteraction,
    }),
  };

  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IDebitmanagment>
  >;
};

export const sendRepossessionDebitManagement = async (
  vehicleId: string,
  overdueDays: number,
  totalDebt: number,
  renter: { _id: string; name: string; whatsapp: string },
  observation: string,
  priority: string
) => {
  const url = `${API_URL}/send-repossession`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      vehicleId,
      overdueDays,
      totalDebt,
      renter,
      observation,
      priority,
    }),
  };

  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IRepossession>
  >;
};
